import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import {Button, Stack, Typography} from "@mui/material";
import LoopIcon from "@mui/icons-material/Loop";
import ErrorIcon from "@mui/icons-material/Error";
import AddIcon from "@mui/icons-material/AddOutlined";
import {CompanySwitchDialog} from "../dialogs/CompanySwitchDialog";
import {CompanyEditDialog} from "../dialogs/CompanyEditDialog";
import {Company} from "../../types/domain/company";
import {authenticatedUserAtom} from "../../store/authenticatedUserStore";
import {CompanyIcon} from "../icons/CompanyIcon";
import {routerCompanyIdAtom, routerFarmIdAtom, routerFieldIdAtom} from "../../store/routerStore";
import {showAllCalendarDatesFeatureFlagAtom} from "../../store/activeDateStore";
import {useCompany} from "../../hooks/companiesHooks";
import {useQueryClient, UseQueryResult} from "@tanstack/react-query";
export interface Props {
    basePath: string;
}

export const CompanyHeader: React.FC<Props> = ({basePath}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [companySwitchDialogOpened, setCompanySwitchDialogOpened] = useState<boolean>(false);
    const [createCompanyDialogOpened, setCreateCompanyDialogOpened] = useState<boolean>(false);
    const company = useCompany();
    const refreshAuthenticatedUser = useSetAtom(authenticatedUserAtom);
    const activeCompanyId = useAtomValue(routerCompanyIdAtom);
    const setFarmId = useSetAtom(routerFarmIdAtom);
    const setFieldId = useSetAtom(routerFieldIdAtom);
    const navigate = useNavigate();
    const [, setShowAllDatesFeatureFlag] = useAtom(showAllCalendarDatesFeatureFlagAtom);

    const onCompanySwitchDialogClose = (isSuccessful: boolean, companyId?: string) => {
        setCompanySwitchDialogOpened(false);
        if (isSuccessful) {
            if (companyId !== activeCompanyId) {
                setShowAllDatesFeatureFlag(undefined);
                setFarmId(undefined);
                setFieldId(undefined);
                navigate({
                    pathname: `${basePath}/${companyId}`,
                    search: location.search,
                });
            }
        }
    };

    const onNewCompanyDialogClose = (isSuccessful: boolean) => {
        setCreateCompanyDialogOpened(false);
        if (isSuccessful) {
            void queryClient.invalidateQueries({queryKey: ["userCompanies"]});
            refreshAuthenticatedUser();
        }
    };

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap="1.125rem" minWidth="0">
                    {company && (
                        <CompanyIcon
                            sx={{
                                width: "1.625rem",
                                height: "1.625rem",
                                stroke: "#5161C1",
                            }}
                        />
                    )}
                    {company?.isError && (
                        <ErrorIcon
                            sx={{
                                height: 36,
                                width: 36,
                            }}
                            color="error"
                        />
                    )}
                    <Typography fontSize={30} lineHeight={1.27} fontWeight={700} textOverflow="ellipsis" noWrap>
                        {renderTitle(company, t)}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap="0.5rem">
                    {basePath === "/organization" && (
                        <Button
                            sx={{whiteSpace: "nowrap"}}
                            startIcon={<AddIcon />}
                            onClick={() => setCreateCompanyDialogOpened(true)}
                        >
                            {t("companies.header.create")}
                        </Button>
                    )}
                    <Button
                        sx={{whiteSpace: "nowrap"}}
                        startIcon={<LoopIcon />}
                        onClick={() => setCompanySwitchDialogOpened(true)}
                    >
                        {t("companies.header.switch")}
                    </Button>
                </Stack>
            </Stack>
            {companySwitchDialogOpened && (
                <CompanySwitchDialog open={companySwitchDialogOpened} onClose={onCompanySwitchDialogClose} />
            )}
            {createCompanyDialogOpened && (
                <CompanyEditDialog mode="create" open={createCompanyDialogOpened} onClose={onNewCompanyDialogClose} />
            )}
        </>
    );
};

const renderTitle = (
    company: UseQueryResult<Company, Error>,
    t: TFunction<"translation", undefined>,
): React.ReactNode | string => {
    if (company.isError) {
        return company.error?.message;
    }

    if (company.isSuccess && company.data) {
        return company.data.properties?.name;
    }

    if (company.isSuccess) {
        return t("companies.header.error_no_company");
    }

    return null;
};
